import React, { useState, useEffect } from 'react';

const TIMELINE_ICONS = {
  CheckCircle2: (c) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/>
      <path d="m9 12 2 2 4-4"/>
    </svg>
  ),
  FileCheck: (c) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <path d="m9 15 2 2 4-4"/>
    </svg>
  ),
  Cpu: (c) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="4" width="16" height="16" rx="2"/>
      <rect x="9" y="9" width="6" height="6"/>
      <path d="M9 2v2M15 2v2M9 20v2M15 20v2M2 9h2M2 15h2M20 9h2M20 15h2"/>
    </svg>
  ),
  UserCheck: (c) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <polyline points="16 11 18 13 22 9"/>
    </svg>
  ),
  Users: (c) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>
    </svg>
  ),
  Award: (c) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="8" r="6"/>
      <path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/>
    </svg>
  ),
  Send: (c) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13"/>
      <polygon points="22 2 15 22 11 13 2 9 22 2"/>
    </svg>
  ),
  Check: (c) => (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  ),
  Clock: (c) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
    </svg>
  ),
};

const AuditTimelineRow = ({ item }) => {
  const [, forceUpdate] = useState(0);

  const getElapsedHours = () => {
    if (!item?.uploadDate) return 0;
    return (new Date() - new Date(item.uploadDate)) / (1000 * 60 * 60);
  };

  const getCompletedSteps = () => {
    const status = item?.status;
    const isPublished = item?.isAudit && item?.decxScore > 0;
    const auditType = item?.isPaid ? 'paid' : 'free';
    const elapsedHours = getElapsedHours();
    if (auditType === 'paid') {
      if (isPublished || status === 'completed') return [1, 2, 3, 4, 5, 6, 7];
      const s = [1];
      if (elapsedHours >= 1)  s.push(2);
      if (elapsedHours >= 3)  s.push(3);
      if (elapsedHours >= 7)  s.push(4);
      if (elapsedHours >= 15) s.push(5);
      if (elapsedHours >= 27) s.push(6);
      return s;
    } else {
      if (isPublished || status === 'completed') return [1, 2, 3, 4, 5];
      if (status === 'processing' || status === 'in_review') return [1, 2, 3, 4];
      if (status === 'pending') return [1, 2];
      return [1];
    }
  };

  const timelineSteps = item?.isPaid ? [
    { id: 1, label: 'Payment Confirmed',  description: 'Order received and payment verified',       icon: 'CheckCircle2', estimatedTime: 'Completed'           },
    { id: 2, label: 'File Validation',    description: 'Checking deck format and accessibility',    icon: 'FileCheck',    estimatedTime: '1 hour'              },
    { id: 3, label: 'AI Analysis',        description: 'Automated content and structure analysis',  icon: 'Cpu',          estimatedTime: '2 hours'             },
    { id: 4, label: 'Expert Assignment',  description: 'Matched with specialized analyst',          icon: 'UserCheck',    estimatedTime: '4 hours'             },
    { id: 5, label: 'Human Review',       description: 'Expert analysis and recommendations',       icon: 'Users',        estimatedTime: '8 hours'             },
    { id: 6, label: 'Quality Assurance',  description: 'Final review and report preparation',       icon: 'Award',        estimatedTime: '12 hours'            },
    { id: 7, label: 'Report Delivered',   description: 'Comprehensive audit report published',      icon: 'Send',         estimatedTime: 'Awaiting publication' },
  ] : [
    { id: 1, label: 'Submission Received', description: 'Deck uploaded successfully',              icon: 'CheckCircle2', estimatedTime: 'Completed'      },
    { id: 2, label: 'File Validation',     description: 'Checking deck format',                    icon: 'FileCheck',    estimatedTime: '1 hour'         },
    { id: 3, label: 'AI Analysis',         description: 'Automated analysis in progress',          icon: 'Cpu',          estimatedTime: '2-3 hours'      },
    { id: 4, label: 'Manual Review',       description: 'Quick expert review',                     icon: 'Users',        estimatedTime: '6-12 hours'     },
    { id: 5, label: 'Report Delivered',    description: 'DecX audit report published',             icon: 'Send',         estimatedTime: '1 business day' },
  ];

  const completedSteps = getCompletedSteps();

  useEffect(() => {
    if (item?.isPaid && !(item?.isAudit && item?.decxScore > 0)) {
      const interval = setInterval(() => forceUpdate(n => n + 1), 60000);
      return () => clearInterval(interval);
    }
  }, [item?.isPaid, item?.isAudit, item?.decxScore]);

  const SUCCESS  = 'var(--color-success, #10b981)';
  const MUTED_FG = 'var(--color-muted-foreground, var(--text-3))';
  const PRIMARY  = 'var(--color-primary, var(--blue-mid))';

  return (
    <div style={{
      background: 'rgba(0,0,0,0.015)', borderRadius: 8,
      border: '0.5px solid var(--divider)', padding: 16, marginTop: 12,
      animation: 'db-expand 0.2s var(--ease) both',
    }}>
      {/* Section heading — Sansation */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        {TIMELINE_ICONS.Clock(PRIMARY)}
        <h3 style={{ fontFamily: "'Sansation', sans-serif", fontSize: 13, fontWeight: 700, color: 'var(--text-1)', margin: 0 }}>
          Audit Timeline — {item?.isPaid ? 'Premium' : 'Free DecX'}
        </h3>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {timelineSteps.map((step, index) => {
          const isCompleted = completedSteps.includes(step.id);
          const isLast = index === timelineSteps.length - 1;
          const IcoSvg = TIMELINE_ICONS[step.icon] || TIMELINE_ICONS.CheckCircle2;
          return (
            <div key={step.id} style={{ position: 'relative' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.2s',
                    background: isCompleted ? 'rgba(16,185,129,0.1)' : 'var(--input-bg)',
                    boxShadow: isCompleted ? '0 0 0 2px rgba(16,185,129,0.2)' : '0 0 0 2px var(--divider)',
                  }}>
                    {IcoSvg(isCompleted ? SUCCESS : MUTED_FG)}
                  </div>
                  {!isLast && (
                    <div style={{
                      position: 'absolute', left: '50%', top: 32,
                      transform: 'translateX(-50%)', width: 2, height: 24,
                      background: isCompleted ? 'rgba(16,185,129,0.3)' : 'var(--divider)',
                    }} />
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0, paddingTop: 2 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 2 }}>
                    {/* Step label — Sansation */}
                    <h4 style={{ fontFamily: "'Sansation', sans-serif", fontSize: 12, fontWeight: 700, margin: 0, color: isCompleted ? 'var(--text-1)' : MUTED_FG }}>
                      {step.label}
                    </h4>
                    {/* Status / time — inherit */}
                    {isCompleted ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, fontWeight: 500, color: SUCCESS, whiteSpace: 'nowrap', fontFamily: 'inherit' }}>
                        {TIMELINE_ICONS.Check(SUCCESS)} Done
                      </span>
                    ) : (
                      <span style={{ fontSize: 11, color: MUTED_FG, whiteSpace: 'nowrap', fontFamily: 'inherit' }}>{step.estimatedTime}</span>
                    )}
                  </div>
                  {/* Description — inherit */}
                  <p style={{ fontFamily: 'inherit', fontSize: 11, color: MUTED_FG, margin: 0 }}>{step.description}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer — inherit */}
      <div style={{ marginTop: 16, paddingTop: 12, borderTop: '0.5px solid var(--divider)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontFamily: 'inherit', fontSize: 11, color: MUTED_FG }}>
            Progress: {completedSteps.length}/{timelineSteps.length} steps
          </span>
          {!(item?.isAudit && item?.decxScore > 0) && (
            <span style={{ fontFamily: 'inherit', fontSize: 11, fontWeight: 500, color: PRIMARY }}>
              {item?.isPaid ? '24-48h delivery' : '1 business day'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditTimelineRow;